import {
  LeadingTrailingTextAccessoriesRowComponent,
  Maybe,
  PresentBottomSheetAction,
  VxlColor,
  VxlTextStackAccessory,
} from '@varo/graphql-schema-types';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { StackedTextAccessoriesProps } from 'shared/components/StackedTextAccessories/StackedTextAccessories';
import { iconKeyForStyleDictionary } from 'shared/components/vxl/icon/Icon/Icon.helpers';
import { VXLComponentId, VXLNode } from 'shared/renderer/types';

import bottomSheetElementResults from './bottomSheetElementResult';
import { BorrowingPowerDependencies } from './types';

const buildAccessory = (
  accessory: Maybe<VxlTextStackAccessory>,
  dependencies: BorrowingPowerDependencies,
  action?: Maybe<PresentBottomSheetAction>,
) => {
  if (!accessory) {
    return null;
  }

  const { showBottomSheet, segmentClient } = dependencies;

  let iconOnPress: Maybe<() => void> = null;
  if (action) {
    iconOnPress = () => {
      showBottomSheet(bottomSheetElementResults(action.elements, dependencies));
      if (action.analyticsEvent) {
        const { name, properties } = parseGQLAnalyticsEvent(
          action.analyticsEvent,
        );
        void segmentClient.logEvent(name, properties);
      }
    };
  }

  const { title, subtitle, icon } = accessory;
  // assume title / label styles will always be in parity:
  const { style } = title.text;
  const textStyle =
    style.color === VxlColor.TextSecondary ? 'secondary' : 'primary';

  const labelText = title.text.copy;
  const titleText = subtitle?.text.copy;

  const labelIconKey = icon ? iconKeyForStyleDictionary(icon) : null;

  return {
    labelText,
    titleText,
    textStyle,
    labelIconKey,
    iconOnPress,
  } satisfies StackedTextAccessoriesProps['leadingAccessory'];
};

const leadingTrailingTextAccessoriesRowComponent = (
  {
    id,
    leading,
    leadingOnClick,
    trailing,
    trailingOnClick,
  }: LeadingTrailingTextAccessoriesRowComponent,
  dependencies: BorrowingPowerDependencies,
): VXLNode => {
  const leadingAccessory = buildAccessory(
    leading,
    dependencies,
    leadingOnClick,
  );
  const trailingAccessory = buildAccessory(
    trailing,
    dependencies,
    trailingOnClick,
  );

  return {
    id: id,
    name: VXLComponentId.STACKED_TEXT_ACCESSORIES,
    props: {
      leadingAccessory,
      trailingAccessory,
    } satisfies StackedTextAccessoriesProps,
  };
};

export default leadingTrailingTextAccessoriesRowComponent;
